import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';

import { AxiosResponse } from 'axios';

import { endPoints, getAlternatesDataSheet, addselectAlternates, getSearchResult, fetchProductSearchHistory, fetchProductFeedback } from './apis';
import { invalidateGetProductDetailsQuery } from '../../createProduct/queries';
import { queryClient } from 'src/app/queryClient';
import { AlternatesDetailsResponse, SearchRefreshResultResponse } from './apiTypes';
import { graphqlRequest } from 'src/app/graphqlClient';

const getalternatesKey = (bomId: string, productId: string) => [endPoints.alternatesDetails(bomId, productId)];
const getSearchKey = (bomId: string, productId: string) => [endPoints.searchDetails(bomId, productId)];

export const cancelGetAlternateDatasheetsRequest = (bomId: string, productId: string) => {
    queryClient.cancelQueries({ queryKey: getalternatesKey(bomId, productId) });
};

export const useGetAlternatesDataSheet = (bomId: string, productId: string, options: Omit<UseQueryOptions<AxiosResponse<AlternatesDetailsResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(getalternatesKey(bomId, productId), () => getAlternatesDataSheet(bomId, productId), options);

export const useGetSearchResults = (bomId: string, productId: string, options: Omit<UseQueryOptions<AxiosResponse<SearchRefreshResultResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(getSearchKey(bomId, productId), () => getSearchResult(bomId, productId), options);

export const useSelectAlaternateMutation = () =>
    useMutation(addselectAlternates, {
        onSuccess: (_res, req) => {
            const { bomId, productId } = req;
            invalidateGetProductDetailsQuery(bomId.toString(), productId.toString());
        }
    });

export const useProductSearchHistoryMutation = () => useMutation(fetchProductSearchHistory);
export const useProductFeedbackMutation = () => useMutation(fetchProductFeedback);

export async function pushSearchSelectedProductEvent({
    alternatePdf,
    productId,
    description,
    documentId,
    hideOriginLine,
    selectionId,
    isBodCreated,
    manufacturer,
    modelNumber,
    rememberSelection,
    sortSuggestedResultsBy,
    manufacturerAndCountCache,
    previouslySelectedDataSheetArrayLength,
    relativeIndex,
    selectedDocumentUrl,
    type,
    specification,
    requirementsCache,
    searchResultId,
    dataOrigin,
    mfgOrder
}: {
    alternatePdf: string | undefined;
    productId: number | null;
    description: string | null;
    documentId: number | null;
    hideOriginLine: boolean | undefined;
    selectionId: number | undefined;
    isBodCreated: boolean | undefined;
    manufacturer: string | undefined;
    modelNumber: string | null;
    rememberSelection: boolean | undefined;
    sortSuggestedResultsBy: string;
    manufacturerAndCountCache: { manufacturers: string; count: number }[];
    previouslySelectedDataSheetArrayLength: number;
    relativeIndex: number | undefined;
    selectedDocumentUrl: string | null | undefined;
    type: string | null;
    specification: string | undefined;
    requirementsCache: any[];
    searchResultId: number | null;
    dataOrigin: string;
    mfgOrder: number;
}) {
    const queryString = `
        mutation PushSearchSelectedProductEvent(
            $alternatePdf: String!,
            $productId: Int!,
            $description: String,
            $documentId: Int,
            $hideOriginLine: Boolean!,
            $selectionId: Int,
            $isBodCreated: Boolean!,
            $manufacturer: String,
            $modelNumber: String,
            $rememberSelection: Boolean!,
            $sortSuggestedResultsBy: String!,
            $manufacturerAndCountCache: jsonb!,
            $previouslySelectedDataSheetArrayLength: Int,
            $relativeIndex: Int!,
            $selectedDocumentUrl: String,
            $type: String,
            $specification: String!,
            $searchResultId: Int!,
            $dataOrigin: String!,
            $mfgOrder: Int,
            $extracted_attributes: jsonb!
        ) {
            events_db {
                insert_pf_search_select_analytics(objects: {
                    alternate_pdf: $alternatePdf,
                    bod_id: $productId,
                    description: $description,
                    document_id: $documentId,
                    hide_origin_line: $hideOriginLine,
                    id: $selectionId,
                    is_bod_created: $isBodCreated,
                    manufacturer: $manufacturer,
                    model_number: $modelNumber,
                    remember_selection: $rememberSelection,
                    selection_type: $sortSuggestedResultsBy,
                    rank: $relativeIndex,
                    result_preferredlist_count: $previouslySelectedDataSheetArrayLength,
                    results_per_mfg: $manufacturerAndCountCache,
                    search_result_id: $searchResultId,
                    selection_type_relative_rank: $relativeIndex,
                    source_pdf: $selectedDocumentUrl,
                    specification: $specification,
                    type: $type,
                    data_origin: $dataOrigin,
                    mfg_order: $mfgOrder,
                    extracted_attributes: $extracted_attributes
                }) {
                    affected_rows
                }
            }
        }
    `;

    const variables = {
        alternatePdf,
        productId,
        description,
        documentId,
        hideOriginLine,
        selectionId,
        isBodCreated,
        manufacturer,
        modelNumber,
        rememberSelection,
        sortSuggestedResultsBy,
        previouslySelectedDataSheetArrayLength,
        relativeIndex,
        selectedDocumentUrl,
        type,
        specification,
        searchResultId,
        dataOrigin,
        mfgOrder,
        extracted_attributes: requirementsCache,
        manufacturerAndCountCache
    };

    return graphqlRequest({ document: queryString, variables });
}
